import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Check, ArrowLeft, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt > 8) {
        setState("timeout");
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
      <div className="noise-overlay" />
      <div className="w-full max-w-md text-center" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-zinc-500 mx-auto mb-6" size={30} />
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
              Confirming your order…
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center mx-auto mb-6">
              <Check size={26} />
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">
              Order confirmed
            </h1>
            <p className="font-body text-sm text-zinc-400 mb-2">
              Thank you for supporting FRE3BRD.
            </p>
            {order?.product_name && (
              <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-8">
                {order.product_name} · ${Number(order.amount).toFixed(2)}
              </p>
            )}
            <p className="font-body text-xs text-zinc-500 mb-8">
              A receipt has been sent to your email. Your order will ship shortly.
            </p>
          </>
        )}

        {state === "timeout" && (
          <>
            <Loader2 className="animate-spin text-zinc-500 mx-auto mb-6" size={26} />
            <h1 className="font-display font-bold text-2xl tracking-tight mb-3">
              Still processing
            </h1>
            <p className="font-body text-sm text-zinc-400 mb-8">
              Your payment is taking a moment to confirm. If it was successful,
              you'll receive an email receipt shortly.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <AlertCircle className="text-zinc-500 mx-auto mb-6" size={30} />
            <h1 className="font-display font-bold text-2xl tracking-tight mb-3">
              Something went wrong
            </h1>
            <p className="font-body text-sm text-zinc-400 mb-8">
              We couldn't confirm this order. You have not been charged if the
              payment didn't complete.
            </p>
          </>
        )}

        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={13} /> Back to FRE3BRD
        </Link>
      </div>
    </div>
  );
}
